import React from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiCheck } from "react-icons/fi";
import SEO from "../components/SEO";
import Layout from "../components/Layout";
import Stats from "../components/Stats";
import ServiceCard from "../components/ServiceCard";
import CTA from "../components/CTA";
import services from "../data/services";
import site from "../data/site";

const Home = () => (
  <Layout>
    <SEO />

    <section className="hero">
      <div className="container">
        <div className="layout">
          <div>
            <span className="eyebrow eyebrow--light">Technology &amp; Internet Services</span>
            <h1>
              Engineered technology for businesses that <span className="accent">refuse to stand still.</span>
            </h1>
            <p className="lead" style={{ color: "rgba(255,255,255,.78)", maxWidth: "560px", marginTop: "1rem" }}>
              {site.shortPitch}
            </p>
            <div className="hero-cta">
              <Link to="/services" className="btn btn--primary">
                Explore our services <FiArrowRight />
              </Link>
              <Link to="/contact" className="btn btn--ghost-light">
                Talk to an engineer
              </Link>
            </div>
          </div>

          <div className="hero-side">
            <div className="tile">
              <div className="num">01 / Build</div>
              <h4>Custom software, networks, cloud</h4>
              <p>Architected and delivered by senior engineers — not outsourced, not assembled from templates.</p>
            </div>
            <div className="tile">
              <div className="num">02 / Operate</div>
              <h4>Managed services with real SLAs</h4>
              <p>24/7 monitoring, incident response and quarterly business reviews under a single accountable team.</p>
            </div>
            <div className="tile">
              <div className="num">03 / Advise</div>
              <h4>Independent technology counsel</h4>
              <p>Strategy, architecture and due diligence from practitioners with no vendor agenda.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <Stats />

    <section className="section">
      <div className="container">
        <div className="split">
          <div className="split-media">
            <span className="glyph">AB</span>
          </div>
          <div>
            <span className="eyebrow">About Atlas</span>
            <h2>A single technology partner across the entire stack.</h2>
            <p>
              Atlas Brokerage exists because mid-market and enterprise customers are tired of stitching together
              eight different suppliers to keep their technology working. We bring software, networking, cloud,
              security and managed operations into one cohesive practice, governed by one accountable team.
            </p>
            <p>
              Our engineers have spent careers inside the industries we now serve — financial services, logistics,
              healthcare, hospitality, public sector. We do not learn your business on your time, and we do not hide
              behind certifications when something difficult needs to be said.
            </p>
            <ul className="feature-list">
              <li><span className="check"><FiCheck /></span> Senior-led delivery with measurable, contracted SLAs</li>
              <li><span className="check"><FiCheck /></span> Vendor-independent advice across cloud, security and software</li>
              <li><span className="check"><FiCheck /></span> Fully documented handover — no black-box lock-in</li>
              <li><span className="check"><FiCheck /></span> Regional presence with full English-language delivery</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className="section section--paper">
      <div className="container">
        <div className="section-title">
          <span className="eyebrow" style={{ margin: "0 auto 1rem", display: "inline-flex" }}>What we do</span>
          <h2>Eleven services, one operating model.</h2>
          <p>From a single CRM rollout to a full IT department on retainer, every engagement runs on the same disciplined delivery model.</p>
        </div>

        <div className="grid grid--3">
          {services.map((s, i) => (
            <ServiceCard key={s.slug} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>

    <section className="section section--dark">
      <div className="container">
        <div className="split">
          <div>
            <span className="eyebrow eyebrow--light">How we work</span>
            <h2>A delivery model designed to be boring on purpose.</h2>
            <p>
              Surprises in technology projects are almost never good. Our delivery process is engineered to make
              progress visible early, problems visible earlier, and decisions defensible to anyone you have to
              report to.
            </p>
          </div>
          <div>
            <div className="process">
              <div className="step">
                <div className="num">01</div>
                <h4>Discover</h4>
                <p>Workshops, system audits and stakeholder interviews translate your goals into a concrete blueprint.</p>
              </div>
              <div className="step">
                <div className="num">02</div>
                <h4>Design</h4>
                <p>Architecture, costing and a phased plan you can show a board — not a brochure.</p>
              </div>
              <div className="step">
                <div className="num">03</div>
                <h4>Deliver</h4>
                <p>Short iterations, demoed regularly, with a single accountable lead from kickoff to handover.</p>
              </div>
              <div className="step">
                <div className="num">04</div>
                <h4>Operate</h4>
                <p>SLA-backed monitoring, support and quarterly reviews — or a clean handover to your team.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="section">
      <div className="container">
        <div className="section-title section-title--left">
          <span className="eyebrow">Industries</span>
          <h2>Built around the businesses we already understand.</h2>
          <p>Our team has shipped systems inside each of these sectors. We bring that context to the first meeting.</p>
        </div>
        <div className="industries">
          {site.industries.map((i) => (
            <span key={i} className="pill">{i}</span>
          ))}
        </div>
      </div>
    </section>

    <CTA />
  </Layout>
);

export default Home;
